import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './WorkingAllianceChart.css';

const VIEWBOX_WIDTH = 360;
const MIN_RESIZED_CHART_PX = 90;
const RESIZE_KEY_STEP_PX = 12;
const MIN_TICK_SPACING = 12;
const RESIZING_BODY_CLASS = 'wai-resizing';

function buildPath(points, xScale, yScale) {
  return points
    .map((point, index) => {
      const command = index === 0 ? 'M' : 'L';
      return `${command} ${xScale(point.clientTurn).toFixed(1)} ${yScale(point.totalScore).toFixed(1)}`;
    })
    .join(' ');
}

function WorkingAllianceChart({
  series = [],
  visibleCount = null,
  collapsed: controlledCollapsed,
  onToggle,
  onInfoOpen,
  viewBoxHeight = 206,
  // Shrink-only resizing: resizeHeight is the chart's pixel height (null means
  // the default aspect-ratio height). Passing onResizeHeightChange enables the handle.
  resizeHeight = null,
  onResizeHeightChange,
}) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [chartWidthPx, setChartWidthPx] = useState(0);
  const chartRef = useRef(null);
  const dragRef = useRef(null);
  const collapsed = controlledCollapsed ?? internalCollapsed;
  const handleToggle = onToggle || (() => setInternalCollapsed((value) => !value));
  const points = series
    .map((row) => ({
      clientTurn: Number(row.client_turn),
      totalScore: Number(row.total_score),
    }))
    .filter((row) => Number.isFinite(row.clientTurn) && Number.isFinite(row.totalScore));
  const hasPoints = points.length > 0;

  useLayoutEffect(() => {
    const node = chartRef.current;
    if (!node) return undefined;
    const updateWidth = () => setChartWidthPx(node.clientWidth);
    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(node);
    return () => resizeObserver.disconnect();
  }, [collapsed, hasPoints]);

  useEffect(() => () => document.body.classList.remove(RESIZING_BODY_CLASS), []);

  const measured = !collapsed && chartWidthPx > 0;
  const resizable = Boolean(onResizeHeightChange) && measured;
  const shrunk = measured && resizeHeight != null;
  const naturalHeightPx = (chartWidthPx * viewBoxHeight) / VIEWBOX_WIDTH;
  const minHeightPx = Math.min(MIN_RESIZED_CHART_PX, naturalHeightPx);
  const displayedHeightPx = shrunk
    ? Math.max(minHeightPx, Math.min(naturalHeightPx, resizeHeight))
    : naturalHeightPx;

  const applyResizeHeight = (nextHeightPx) => {
    const clamped = Math.max(minHeightPx, Math.min(naturalHeightPx, nextHeightPx));
    // Back at full size means "default" again, so it keeps tracking the panel width.
    onResizeHeightChange(clamped >= naturalHeightPx - 0.5 ? null : clamped);
  };

  const handleResizePointerDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { startY: event.clientY, startHeight: displayedHeightPx };
    document.body.classList.add(RESIZING_BODY_CLASS);
  };

  const handleResizePointerMove = (event) => {
    if (!dragRef.current) return;
    applyResizeHeight(dragRef.current.startHeight + (event.clientY - dragRef.current.startY));
  };

  const endResizeDrag = () => {
    dragRef.current = null;
    document.body.classList.remove(RESIZING_BODY_CLASS);
  };

  const handleResizeKeyDown = (event) => {
    if (event.key === 'ArrowUp') {
      applyResizeHeight(displayedHeightPx - RESIZE_KEY_STEP_PX);
    } else if (event.key === 'ArrowDown') {
      applyResizeHeight(displayedHeightPx + RESIZE_KEY_STEP_PX);
    } else if (event.key === 'Home') {
      applyResizeHeight(minHeightPx);
    } else if (event.key === 'End') {
      applyResizeHeight(naturalHeightPx);
    } else {
      return;
    }
    event.preventDefault();
  };

  const resizeHandle = resizable ? (
    <div
      className="wai-resize-handle"
      role="separator"
      aria-orientation="horizontal"
      aria-label="Resize working alliance chart"
      aria-valuemin={Math.round(minHeightPx)}
      aria-valuemax={Math.round(naturalHeightPx)}
      aria-valuenow={Math.round(displayedHeightPx)}
      tabIndex={0}
      title="Drag to resize"
      onPointerDown={handleResizePointerDown}
      onPointerMove={handleResizePointerMove}
      onPointerUp={endResizeDrag}
      onPointerCancel={endResizeDrag}
      onLostPointerCapture={endResizeDrag}
      onKeyDown={handleResizeKeyDown}
    >
      <span className="wai-resize-handle__grip" aria-hidden="true" />
    </div>
  ) : null;

  if (!hasPoints) {
    return (
      <section className={`wai-section${resizable ? ' wai-section--resizable' : ''}`} aria-label="Working alliance trend">
        <div className="wai-panel__header">
          <div className="wai-panel__title-row">
            <h4 className="wai-panel__title">
              <span className="wai-panel__title-full">Working Alliance</span>
              <span className="wai-panel__title-short">WAI</span>
            </h4>
            {!collapsed && (
              <button
                type="button"
                className="wai-panel__info-btn"
                onClick={() => onInfoOpen && onInfoOpen()}
                aria-label="Learn about Working Alliance"
                title="Learn about Working Alliance"
              >
                i
              </button>
            )}
          </div>
          <button
            type="button"
            className="wai-panel__toggle"
            onClick={handleToggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand working alliance chart' : 'Collapse working alliance chart'}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              {collapsed ? <polyline points="6 15 12 9 18 15" /> : <polyline points="6 9 12 15 18 9" />}
            </svg>
          </button>
        </div>
        {!collapsed && (
          <div
            className="wai-empty-state"
            ref={chartRef}
            // Match the chart's height so the panel does not jump when scores arrive.
            style={measured ? { height: displayedHeightPx, minHeight: 0, boxSizing: 'border-box' } : undefined}
          >
            Working alliance could not be scored for this turn.
          </div>
        )}
        {resizeHandle}
      </section>
    );
  }

  const width = VIEWBOX_WIDTH;
  // Grow the viewBox with the pixel height so labels and dots keep their size
  // instead of being squashed.
  const height = shrunk
    ? (displayedHeightPx * VIEWBOX_WIDTH) / chartWidthPx
    : viewBoxHeight;
  const pad = { top: 16, right: 16, bottom: 24, left: 34 };
  const chartWidth = width - pad.left - pad.right;
  const chartHeight = height - pad.top - pad.bottom;
  const xMax = Math.max(1, ...points.map((point) => point.clientTurn));
  const yMin = 0;
  const yMax = 7;
  const xScale = (turn) => pad.left + (turn / xMax) * chartWidth;
  const yScale = (score) => {
    const bounded = Math.max(yMin, Math.min(yMax, score));
    return pad.top + ((yMax - bounded) / (yMax - yMin)) * chartHeight;
  };
  const clampedVisibleCount = Math.max(
    0,
    Math.min(points.length, visibleCount == null ? points.length : visibleCount)
  );
  const activePoints = points.slice(0, clampedVisibleCount || points.length);
  const futurePoints = clampedVisibleCount < points.length
    ? points.slice(Math.max(0, clampedVisibleCount - 1))
    : [];
  const activePath = buildPath(activePoints, xScale, yScale);
  const futurePath = futurePoints.length > 1 ? buildPath(futurePoints, xScale, yScale) : '';
  // Track the volley being viewed rather than the newest one, so stepping back
  // through turns moves the readout with the solid part of the line.
  const latest = points[points.length - 1];
  const viewedPoint = activePoints[activePoints.length - 1] || latest;
  const isViewingLatest = viewedPoint === latest;
  // Drop the 1 tick once it would collide with the 0 tick on a shrunken chart.
  const ticks = yScale(0) - yScale(1) < MIN_TICK_SPACING ? [0, 3, 5, 7] : [0, 1, 3, 5, 7];

  return (
    <section className={`wai-section${resizable ? ' wai-section--resizable' : ''}`} aria-label="Working alliance trend">
      <div className="wai-panel__header">
        <div className="wai-panel__title-row">
          <h4 className="wai-panel__title">
            <span className="wai-panel__title-full">Working Alliance</span>
            <span className="wai-panel__title-short">WAI</span>
          </h4>
          {!collapsed && (
            <button
              type="button"
              className="wai-panel__info-btn"
              onClick={() => onInfoOpen && onInfoOpen()}
              aria-label="Learn about Working Alliance"
              title="Learn about Working Alliance"
            >
              i
            </button>
          )}
        </div>
        <div className="wai-panel__header-right">
          <div className="wai-panel__latest">
            <span className="wai-panel__latest-label">
              {isViewingLatest ? 'Latest' : `Turn ${viewedPoint.clientTurn}`}
            </span>
            <strong className="wai-panel__latest-value">{viewedPoint.totalScore.toFixed(1)}</strong>
          </div>
          <button
            type="button"
            className="wai-panel__toggle"
            onClick={handleToggle}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand working alliance chart' : 'Collapse working alliance chart'}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              {collapsed ? <polyline points="6 15 12 9 18 15" /> : <polyline points="6 9 12 15 18 9" />}
            </svg>
          </button>
        </div>
      </div>
      {!collapsed && <div className="wai-header-divider" aria-hidden="true" />}
      {!collapsed && (
        <div className="wai-chart" ref={chartRef}>
          <svg className="wai-chart__svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Working alliance total score trend">
            <g className="wai-chart__grid">
              {ticks.map((tick) => {
                const y = yScale(tick).toFixed(1);
                return (
                  <g key={tick}>
                    <line x1={pad.left} y1={y} x2={width - pad.right} y2={y} />
                    <text x={pad.left - 8} y={Number(y) + 4} textAnchor="end">{tick}</text>
                  </g>
                );
              })}
            </g>
            <line className="wai-chart__axis" x1={pad.left} y1={height - pad.bottom} x2={width - pad.right} y2={height - pad.bottom} />
            <line className="wai-chart__axis" x1={pad.left} y1={pad.top} x2={pad.left} y2={height - pad.bottom} />
            <text className="wai-chart__axis-label" x={pad.left} y={height - 8}>0</text>
            <text className="wai-chart__axis-label" x={width - pad.right} y={height - 8} textAnchor="end">{xMax}</text>
            {futurePath ? <path className="wai-chart__line wai-chart__line--future" d={futurePath} /> : null}
            <path className="wai-chart__line" d={activePath} />
            {points.map((point, index) => {
              const x = xScale(point.clientTurn);
              const y = yScale(point.totalScore);
              const isFuturePoint = index >= clampedVisibleCount;
              return (
                <g
                  key={`${point.clientTurn}-${point.totalScore}`}
                  className={`wai-chart__point-group${isFuturePoint ? ' wai-chart__point-group--future' : ''}`}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  aria-label={`Client turn ${point.clientTurn}: ${point.totalScore.toFixed(1)}`}
                >
                  <circle className="wai-chart__hit-area" cx={x.toFixed(1)} cy={y.toFixed(1)} r="11" />
                  <circle
                    className={`wai-chart__point${hoveredIndex === index ? ' wai-chart__point--hovered' : ''}`}
                    cx={x.toFixed(1)}
                    cy={y.toFixed(1)}
                    r={hoveredIndex === index ? '4.5' : '3.5'}
                  />
                </g>
              );
            })}
            {hoveredIndex !== null && points[hoveredIndex] ? (() => {
              const point = points[hoveredIndex];
              const x = xScale(point.clientTurn);
              const y = yScale(point.totalScore);
              const label = point.totalScore.toFixed(1);
              const gap = 10;
              const labelWidth = 30;
              const showBelow = y - gap - 12 < 2;
              const textY = showBelow ? y + gap + 12 : y - gap;
              const textX = Math.max(labelWidth / 2 + 2, Math.min(width - labelWidth / 2 - 2, x));
              return (
                <text
                  className="wai-chart__tooltip-label"
                  x={textX.toFixed(1)}
                  y={textY.toFixed(1)}
                  textAnchor="middle"
                  pointerEvents="none"
                >
                  {label}
                </text>
              );
            })() : null}
          </svg>
        </div>
      )}
      {resizeHandle}
    </section>
  );
}

export default WorkingAllianceChart;
